import { ethers } from 'ethers';
import Minter from 'contracts/ObscuraMinter.json';
import MinterV2 from 'contracts/MinterV2.json';
import MintPass from 'contracts/ObscuraMintPass.json';
import Curated from 'contracts/ObscuraCurated.json';
import MinterV3 from 'contracts/MinterV3.json';
import MinterV3C from 'contracts/MinterV3C.json';
import MinterFoundry from 'contracts/FoundryMinter.json';
import axios from 'axios';
import MinterCommunity from 'contracts/CommunityMinter2.json';
import Web3Modal from 'web3modal';
import WalletConnectProvider from '@walletconnect/web3-provider';
import { toast } from 'react-toast';
import { TX_STATE } from '../components/TxLoader';
import { COMMISSIONS } from './commissions';
import { getCuratedContract, getMagnumContract, getZoomContract } from './contracts';
import { zoomHasMintPassClaimedForThisProject, zoomMpToTokenClaimed } from "./zoom2";

interface ArweaveMetadata {
  name: string;
  image: string;
  attributes: Attributes;
}

const arweaveCache: Record<string, ArweaveMetadata> = {};

export const getMetadata = async (tokenId, cid): Promise<ArweaveMetadata> => {
  if (!cid || !tokenId) {
    throw new Error('CID and TokenID are required');
  }
  const metadataLink = `https://arweave.net/${cid}/${tokenId}`;

  if (arweaveCache[metadataLink]) {
    return arweaveCache[metadataLink];
  }

  let response;
  try {
    response = await axios.get(metadataLink);
    arweaveCache[metadataLink] = {
      name: response.data.name,
      image: response.data.image,
      attributes: response.data.attributes,
    };
    return arweaveCache[metadataLink];
  } catch (error) {
    throw new Error(error);
  }
};

const WEB3_MODAL_CONFIG = {
  network: process.env.NEXT_PUBLIC_NETWORK,
  cacheProvider: true,
  providerOptions: {
    walletconnect: {
      package: WalletConnectProvider,
      options: {
        infuraId: process.env.NEXT_PUBLIC_INFURA_ID,
      },
    },
  },
};
export const getClaimedTokens = async () => {
  const provider = new ethers.providers.JsonRpcProvider(
    `https://${process.env.NEXT_PUBLIC_NETWORK}.infura.io/v3/${process.env.NEXT_PUBLIC_INFURA_ID}`
  );
  const CURATED_ADDRESS = process.env.NEXT_PUBLIC_CURATED_CONTRACT;
  const CURATED_ABI = Curated.abi;

  let curatedContract;
  try {
    curatedContract = await new ethers.Contract(
      CURATED_ADDRESS,
      CURATED_ABI,
      provider
    );
  } catch (error) {
    console.error('Failed to get contract', error);
  }

  const CONTRACT_START = 13959854; // hardcoded bc vercel not picking up

  const events = await curatedContract.queryFilter(
    {
      address: curatedContract.address,
      topics: [
        '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef',
      ],
    },
    CONTRACT_START,
    CONTRACT_START + 60000
  );

  /* FILTER CLAIMED */
  const claimed = [];
  for (let i = 0; i < events.length; i++) {
    claimed.push(events[i].args.tokenId.toNumber());
  }

  /* HACK FOR CRITICAL BUG. DO NOT REMOVE. */
  for (let i = 51; i < 56; i++) {
    const DIVIDER = 100_000;
    claimed.push(i + DIVIDER);
  }

  /*
    `allTokens` is a list of all IDs: 100001-100055
    `claimed` is a list of all IDs that have been minted (at the moment of this commit only 3 tokens have been minted):
  */
  return claimed;
};

export const checkCuratedSaleLive = async (projectId) => {
  const saleActive = await getCuratedContract()
    .projects(projectId)
    .then((projectInfo) => {
      return projectInfo.isSaleActive;
    })
    .catch((e) => console.error(e));

  return saleActive;
};

export const checkMagnumSaleLive = async (projectId) => {
  const saleActive = await getMagnumContract()
    .projects(projectId)
    .then((projectInfo) => {
      return projectInfo.isSaleActive;
    })
    .catch((e) => console.error(e));

  return saleActive;
};

export const mintNFT = async (projectId, setTxState) => {
  const web3Modal = new Web3Modal(WEB3_MODAL_CONFIG);
  const connection = await web3Modal.connect();
  const provider = new ethers.providers.Web3Provider(connection);
  const signer = provider.getSigner();

  let minterAddress;
  let minterAbi;
  let minterContract;

  if (Number(projectId) <= 2) {
    minterAddress = process.env.NEXT_PUBLIC_MINTERV2_CONTRACT;
    minterAbi = MinterV2.abi;
  } else {
    // New minter contract for projectId 3 and beyond
    minterAddress = process.env.NEXT_PUBLIC_MINTERV3C_CONTRACT;
    minterAbi = MinterV3C.abi;
  }

  try {
    minterContract = await new ethers.Contract(
      minterAddress,
      minterAbi,
      signer
    );
  } catch (error) {
    console.error('Failed to get contracts', error);
    handleError(error);
  }

  const correctNetwork = await checkNetwork(provider);
  if (!correctNetwork) return;

  const gasLimit = await minterContract.estimateGas
    .mint(projectId)
    .catch(handleError);

  if (gasLimit) {
    setTxState({ state: TX_STATE.APPROVE });

    await minterContract
      .mint(projectId, { gasLimit: 100000 + Number(gasLimit) })
      .then((tx) => {
        setTxState({ state: TX_STATE.IN_PROGRESS, txHash: tx.hash });

        return tx.wait().then((receipt) => {
          console.log('txReceipt: ', receipt);
          if (receipt && receipt.status === 1) {
            toast.success('Successfully Minted NFT');
            setTxState({ state: null, txHash: null });
          } else {
            toast.error('Transaction Failed');
            setTxState({ state: null, txHash: null });
          }
        });
      })
      .catch(handleError);
  }
  setTxState({ state: null });
};

export const mintNFTMagnum = async (projectId, setTxState) => {
  const web3Modal = new Web3Modal(WEB3_MODAL_CONFIG);
  const connection = await web3Modal.connect();
  const provider = new ethers.providers.Web3Provider(connection);
  const signer = provider.getSigner();
  const MINTERV3_ADDRESS = process.env.NEXT_PUBLIC_MINTERV3_CONTRACT;
  const MINTERV3_ABI = MinterV3.abi;

  let minterV3Contract;

  try {
    minterV3Contract = await new ethers.Contract(
      MINTERV3_ADDRESS,
      MINTERV3_ABI,
      signer
    );
  } catch (error) {
    console.error('Failed to get contracts', error);
    handleError(error);
  }

  const correctNetwork = await checkNetwork(provider);
  if (!correctNetwork) return;

  const saleActive = await minterV3Contract
    .projects(projectId)
    .then((projectInfo) => projectInfo.isSaleActive)
    .catch((e) => console.error(e));

  if (saleActive == false) {
    toast.error('Sale not active');
    return;
  }

  const gasLimit = await minterV3Contract.estimateGas
    .mint(projectId)
    .catch(handleError);

  if (gasLimit) {
    setTxState({ state: TX_STATE.APPROVE });

    await minterV3Contract
      .mint(projectId, { gasLimit: 100000 + Number(gasLimit) })
      .then((tx) => {
        setTxState({ state: TX_STATE.IN_PROGRESS, txHash: tx.hash });

        return tx.wait().then((receipt) => {
          console.log('txReceipt: ', receipt);
          if (receipt && receipt.status === 1) {
            toast.success('Successfully Minted NFT');
            setTxState({ state: null, txHash: null });
          } else {
            toast.error('Transaction Failed');
            setTxState({ state: null, txHash: null });
          }
        });
      })
      .catch(handleError);
  }
  setTxState({ state: null });
};

export const mintNFTFoundry = async (projectId, setTxState) => {
  const web3Modal = new Web3Modal(WEB3_MODAL_CONFIG);
  const connection = await web3Modal.connect();
  const provider = new ethers.providers.Web3Provider(connection);
  const signer = provider.getSigner();
  const address = process.env.NEXT_PUBLIC_FOUNDRY_MINTER_CONTRACT;
  const abi = MinterFoundry.abi;

  let contract;

  try {
    contract = await new ethers.Contract(address, abi, signer);
  } catch (error) {
    console.error('Failed to get contracts', error);
    handleError(error);
  }

  const correctNetwork = await checkNetwork(provider);
  if (!correctNetwork) return;

  const saleActive = await contract
    .projects(projectId)
    .then((projectInfo) => projectInfo.isSaleActive)
    .catch((e) => console.error(e));

  if (saleActive == false) {
    toast.error('Sale not active');
    return;
  }

  const gasLimit = await contract.estimateGas
    .mint(projectId)
    .catch(handleError);

  if (gasLimit) {
    setTxState({ state: TX_STATE.APPROVE });

    await contract
      .mint(projectId, { gasLimit: 100000 + Number(gasLimit) })
      .then((tx) => {
        setTxState({ state: TX_STATE.IN_PROGRESS, txHash: tx.hash });

        return tx.wait().then((receipt) => {
          console.log('txReceipt: ', receipt);
          if (receipt && receipt.status === 1) {
            toast.success('Successfully Minted NFT');
            setTxState({ state: null, txHash: null });
          } else {
            toast.error('Transaction Failed');
            setTxState({ state: null, txHash: null });
          }
        });
      })
      .catch(handleError);
  }
  setTxState({ state: null });
};

export const mintNFTCommunity = async (projectId, setTxState) => {
  const web3Modal = new Web3Modal(WEB3_MODAL_CONFIG);
  const connection = await web3Modal.connect();
  const provider = new ethers.providers.Web3Provider(connection);
  const signer = provider.getSigner();
  const address = process.env.NEXT_PUBLIC_COMMUNITY_MINTER_CONTRACT;
  const abi = MinterCommunity.abi;

  let contract;

  try {
    contract = await new ethers.Contract(address, abi, signer);
  } catch (error) {
    console.error('Failed to get contracts', error);
    handleError(error);
  }

  const correctNetwork = await checkNetwork(provider);
  if (!correctNetwork) return;

  const saleActive = await contract
    .projects(projectId)
    .then((projectInfo) => projectInfo.isSaleActive)
    .catch((e) => console.error(e));

  if (saleActive == false) {
    toast.error('Sale not active');
    return;
  }

  const gasLimit = await contract.estimateGas
    .mint(projectId)
    .catch(handleError);

  if (gasLimit) {
    setTxState({ state: TX_STATE.APPROVE });

    await contract
      .mint(projectId, { gasLimit: 100000 + Number(gasLimit) })
      .then((tx) => {
        setTxState({ state: TX_STATE.IN_PROGRESS, txHash: tx.hash });

        return tx.wait().then((receipt) => {
          console.log('txReceipt: ', receipt);
          if (receipt && receipt.status === 1) {
            toast.success('Successfully Minted NFT');
            setTxState({ state: null, txHash: null });
          } else {
            toast.error('Transaction Failed');
            setTxState({ state: null, txHash: null });
          }
        });
      })
      .catch(handleError);
  }
  setTxState({ state: null });
};

const checkNetwork = async (provider) => {
  const { chainId } = await provider.getNetwork();

  if (chainId) {
    if (process.env.NEXT_PUBLIC_NETWORK == 'rinkeby' && chainId != 4) {
      toast.error('Please switch to rinkeby');
      return false;
    } else if (process.env.NEXT_PUBLIC_NETWORK == 'mainnet' && chainId != 1) {
      toast.error('Please switch to mainnet');
      return false;
    }
  }

  return true;
};

const handleError = (e) => {
  if (e.error && e.error.message) {
    toast.error(e.error.message);
  } else if (e.message) {
    toast.error(e.message);
  } else if (e.reason) {
    toast.error(e.reason);
  }
};

export const claimNFT = async (tokenId, setTxState) => {
  const web3Modal = new Web3Modal(WEB3_MODAL_CONFIG);
  const connection = await web3Modal.connect();
  const provider = new ethers.providers.Web3Provider(connection);
  const signer = provider.getSigner();
  const MINTER_ADDRESS = process.env.NEXT_PUBLIC_MINTER_CONTRACT;
  const MINTER_ABI = Minter.abi;

  let minterContract;
  try {
    minterContract = await new ethers.Contract(
      MINTER_ADDRESS,
      MINTER_ABI,
      signer
    );
  } catch (error) {
    console.error('Failed to get contracts', error);
  }

  /* MINTING LOGIC */
  const DIVIDER = 100000;
  const newTokenId = tokenId - DIVIDER;

  const correctNetwork = await checkNetwork(provider);
  if (!correctNetwork) return;

  const gasLimit = await minterContract.estimateGas
    .claimWithSeasonPassBySelect(1, 1, newTokenId)
    .catch(handleError);

  if (gasLimit) {
    setTxState({ state: TX_STATE.APPROVE });
    await minterContract
      .claimWithSeasonPassBySelect(1, 1, newTokenId, {
        gasLimit: 100000 + Number(gasLimit),
      })
      .then((tx) => {
        setTxState({ state: TX_STATE.IN_PROGRESS, txHash: tx.hash });

        return tx.wait().then((receipt) => {
          console.log('txReceipt: ', receipt);
          if (receipt && receipt.status === 1) {
            toast.success('Successfully Claimed NFT');
            setTxState({ state: null, txHash: null });
          } else {
            toast.error('Transaction Failed');
            setTxState({ state: null, txHash: null });
          }
        });
      })
      .catch(handleError);
  }
  setTxState({ state: null });
};

export type Attributes = Array<{ trait_type: string; value: string }>;

export interface SeasonPassToken {
  id: number;
  tokenUri: string;
  commission: COMMISSIONS;
}

export const getMintPassClaimStatus = async (
  commission: COMMISSIONS,
  tokenID: number,
  projectID: number
) => {
  switch (commission) {
    case COMMISSIONS.curated: {
      // TODO: is there a more efficient way to check claim status for all projects
      return await getCuratedContract().hasMintPassClaimedForThisProject(
        tokenID,
        projectID
      );
    }
    case COMMISSIONS.magnum: {
      return (
        (await getMagnumContract().mpToTokenClaimed(projectID, tokenID)) ??
        false
      );
    }
    default:
      return false;
  }
};


export const getAllMintPassClaimStatus = async (
  commission: COMMISSIONS,
  tokenIDs: number[],
  projectID: number
) => {
  switch (commission) {
    case COMMISSIONS.curated: {
      return await zoomHasMintPassClaimedForThisProject(
        getZoomContract(),
        getCuratedContract(),
        tokenIDs,
        projectID
      );
    }
    case COMMISSIONS.magnum: {
      return  await zoomMpToTokenClaimed(
        getZoomContract(),
        getMagnumContract(),
        tokenIDs,
        projectID
      );
    }
    default:
      return false;
  }
};

export const getMintPasses = async () => {
  const provider = new ethers.providers.JsonRpcProvider(
    `https://${process.env.NEXT_PUBLIC_NETWORK}.infura.io/v3/${process.env.NEXT_PUBLIC_INFURA_ID}`
  );

  try {
    const passContract = await new ethers.Contract(
      process.env.NEXT_PUBLIC_MINT_PASS_CONTRACT,
      MintPass.abi,
      provider
    );

    const curatedPasses = Array.from(
      { length: 100055 - 100001 + 1 },
      (_, i) => 100001 + i
    );
    const magnumPasses = Array.from(
      { length: 200055 - 200001 + 1 },
      (_, i) => 200001 + i
    );

    const passes: Record<string, SeasonPassToken> = {};
    await Promise.all(
      curatedPasses.map(async (tokenID) => {
        try {
          const tokenUri = await passContract.tokenURI(tokenID);

          passes[tokenID] = {
            id: tokenID,
            tokenUri,
            commission: COMMISSIONS.curated,
          };
        } catch (e) {
          // prevent errors from a single pass stopping rendering of whole page
          console.error(e);
        }
      })
    );
    await Promise.all(
      magnumPasses.map(async (tokenID) => {
        try {
          const tokenUri = await passContract.tokenURI(tokenID);

          passes[tokenID] = {
            id: tokenID,
            tokenUri,
            commission: COMMISSIONS.magnum,
          };
        } catch (e) {
          // prevent errors from a single pass stopping rendering of whole page
          console.error(e);
        }
      })
    );

    return Object.values(passes);
  } catch (error) {
    console.error('Failed to get contract', error);
  }
};
